import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Collapse,
    Alert,
    CircularProgress,
    Paper,
    TablePagination,
} from '@mui/material';
import {
    ExpandMore,
    ExpandLess,
    CheckCircle,
    Error,
    AccessTime,
    Warning,
    RestartAlt,
} from '@mui/icons-material';
import { useNotify, useTranslate } from 'react-admin';
import { apiClient } from '../../utils/apiClient';

interface RebootAuditRecord {
    id: string;
    chargerId: string;
    stationId: string;
    resetType: 'Soft' | 'Hard';
    forceReboot: boolean;
    blockedBySessions: boolean;
    activeSessionIds: string[];
    requestedBy: string;
    requestedByEmail?: string;
    requestedByName?: string;
    requestReason: string;
    requestNotes?: string;
    requestedAt: string;
    ocppStatus?: 'Accepted' | 'Rejected' | 'Timeout' | 'Error';
    ocppResponse?: any;
    completedAt?: string;
    errorMessage?: string;
}

interface RebootAuditLogProps {
    chargerId: string;
}

// Status colors
const getStatusColor = (status?: string): 'success' | 'error' | 'warning' | 'default' => {
    if (!status) return 'default';
    switch (status) {
        case 'Accepted': return 'success';
        case 'Rejected': return 'error';
        case 'Timeout': return 'warning';
        case 'Error': return 'error';
        default: return 'default';
    }
};

// Status icons
const getStatusIcon = (status?: string) => {
    if (!status) return <AccessTime />;
    switch (status) {
        case 'Accepted': return <CheckCircle />;
        case 'Rejected': return <Error />;
        case 'Timeout': return <AccessTime />;
        case 'Error': return <Error />;
        default: return <AccessTime />;
    }
};

// Reason labels
const getReasonLabel = (reason: string): string => {
    const reasonLabels: Record<string, string> = {
        unresponsive: 'Charger Unresponsive',
        firmware_update: 'Firmware Update',
        network_issue: 'Network Connectivity Issue',
        emergency: 'Emergency Maintenance',
        other: 'Other',
    };
    return reasonLabels[reason] || reason;
};

const AuditRow = ({ record }: { record: RebootAuditRecord }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <>
            <TableRow hover>
                <TableCell>
                    <IconButton
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    {new Date(record.requestedAt).toLocaleString()}
                </TableCell>
                <TableCell>
                    <Chip
                        label={record.resetType}
                        size="small"
                        color={record.resetType === 'Hard' ? 'error' : 'info'}
                        icon={<RestartAlt />}
                    />
                </TableCell>
                <TableCell>
                    {record.ocppStatus ? (
                        <Chip
                            label={record.ocppStatus}
                            size="small"
                            color={getStatusColor(record.ocppStatus)}
                            icon={getStatusIcon(record.ocppStatus)}
                        />
                    ) : (
                        <Chip label="Pending" size="small" color="default" />
                    )}
                </TableCell>
                <TableCell>
                    {getReasonLabel(record.requestReason)}
                </TableCell>
                <TableCell>
                    {record.requestedByEmail || record.requestedBy}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: { xs: 1, sm: 2 } }}>
                            <Typography variant="h6" gutterBottom component="div">
                                Reboot Details
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                                {record.forceReboot && (
                                    <Alert severity="warning" icon={<Warning />}>
                                        <strong>Force Reboot:</strong> Active sessions were forcefully interrupted
                                    </Alert>
                                )}

                                {record.blockedBySessions && !record.forceReboot && (
                                    <Alert severity="info">
                                        <strong>Blocked by Active Sessions:</strong> Reboot was not executed due to active charging sessions
                                    </Alert>
                                )}

                                {record.activeSessionIds && record.activeSessionIds.length > 0 && (
                                    <Alert severity="info">
                                        <strong>Active Sessions ({record.activeSessionIds.length}):</strong>{' '}
                                        {record.activeSessionIds.join(', ')}
                                    </Alert>
                                )}

                                {record.requestNotes && (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            Notes:
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {record.requestNotes}
                                        </Typography>
                                    </Box>
                                )}

                                {record.errorMessage && (
                                    <Alert severity="error">
                                        <strong>Error:</strong> {record.errorMessage}
                                    </Alert>
                                )}

                                {record.completedAt && (
                                    <Typography variant="caption" color="text.secondary">
                                        <strong>Completed:</strong> {new Date(record.completedAt).toLocaleString()}
                                    </Typography>
                                )}

                                {record.ocppResponse && (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            OCPP Response:
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f5f5f5' }}>
                                            <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto', maxWidth: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {JSON.stringify(record.ocppResponse, null, 2)}
                                            </pre>
                                        </Paper>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

export const RebootAuditLog: React.FC<RebootAuditLogProps> = ({ chargerId }) => {
    const [loading, setLoading] = useState(true);
    const [records, setRecords] = useState<RebootAuditRecord[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const notify = useNotify();
    const translate = useTranslate();

    useEffect(() => {
        const fetchAuditLogs = async () => {
            try {
                setLoading(true);
                // Fetch reboot audit logs for this charger with pagination
                const response = await apiClient.get(
                    `/api/backend/charger-reboot-audit?filter=${JSON.stringify({
                        charger_id: chargerId
                    })}&sort=requested_at&order=DESC&page=${page + 1}&perPage=${rowsPerPage}`
                ) as { data?: any[]; total?: number };

                // Map snake_case to camelCase
                const mappedRecords: RebootAuditRecord[] = (response.data || []).map((record: any) => ({
                    id: record.id,
                    chargerId: record.charger_id,
                    stationId: record.station_id,
                    resetType: record.reset_type,
                    forceReboot: record.force_reboot,
                    blockedBySessions: record.blocked_by_sessions,
                    activeSessionIds: record.active_session_ids || [],
                    requestedBy: record.requested_by,
                    requestedByEmail: record.requested_by_email,
                    requestedByName: record.requested_by_name,
                    requestReason: record.request_reason,
                    requestNotes: record.request_notes,
                    requestedAt: record.requested_at,
                    ocppStatus: record.ocpp_status,
                    ocppResponse: record.ocpp_response,
                    completedAt: record.completed_at,
                    errorMessage: record.error_message,
                }));

                setRecords(mappedRecords);
                setTotal(response.total || 0);
            } catch (error: any) {
                console.error('Failed to fetch reboot audit logs:', error);
                notify('Failed to load reboot history', { type: 'error' });
            } finally {
                setLoading(false);
            }
        };

        if (chargerId) {
            fetchAuditLogs();
        }
    }, [chargerId, page, rowsPerPage, notify]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (records.length === 0) {
        return (
            <Alert severity="info">
                {translate('resources.chargers.show.noRebootHistory')}
            </Alert>
        );
    }

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <RestartAlt sx={{ mr: 2, color: '#FF9800' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Reboot History ({total})
                    </Typography>
                </Box>

                <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell width="50px" />
                                <TableCell>Date & Time</TableCell>
                                <TableCell>Reset Type</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell>Requested By</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {records.map((record) => (
                                <AuditRow key={record.id} record={record} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    sx={{ borderTop: '1px solid #e0e0e0', mt: 2 }}
                />
            </CardContent>
        </Card>
    );
};
